"use client"

import { useState, useEffect, useRef } from "react"
import { usePostHog } from "posthog-js/react"
import { Pause, Play, Plus, RotateCcw, X } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "cooking-temperature-converter",
  "recipe-measurement-converter",
  "recipe-serving-size-adjuster",
  "refrigerator-organization-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

interface Timer {
  id: number
  name: string
  minutes: number
  seconds: number
  totalSeconds: number
  remainingSeconds: number
  isRunning: boolean
  isComplete: boolean
}

const PRESETS: { label: string; minutes: number; note: string }[] = [
  { label: "Soft-boiled egg", minutes: 6, note: "Large egg, boiling start" },
  { label: "Hard-boiled egg", minutes: 12, note: "Large egg, boiling start" },
  { label: "Pasta al dente", minutes: 9, note: "Dried, varies by shape" },
  { label: "White rice", minutes: 18, note: "Long grain, simmer" },
  { label: "Steak rest", minutes: 5, note: "After cooking" },
  { label: "Bread proof", minutes: 60, note: "Room temp" },
]

export function KitchenTimerPageClient() {
  const posthog = usePostHog()
  const meta = TOOL_METADATA["kitchen-timer"]

  const [timers, setTimers] = useState<Timer[]>([])
  const [newTimerName, setNewTimerName] = useState("")
  const [newTimerMinutes, setNewTimerMinutes] = useState("")
  const [newTimerSeconds, setNewTimerSeconds] = useState("")
  const intervalRefs = useRef<{ [key: number]: ReturnType<typeof setInterval> }>({})

  useEffect(() => {
    const refs = intervalRefs.current
    return () => {
      Object.values(refs).forEach((id) => clearInterval(id))
    }
  }, [])

  const addTimer = (presetName?: string, presetMinutes?: number) => {
    const minutes = presetMinutes ?? (readCount(newTimerMinutes, { min: 1, max: 999 }) || 0)
    const seconds = presetMinutes ? 0 : (readCount(newTimerSeconds, { min: 1, max: 59 }) || 0)
    const totalSeconds = minutes * 60 + seconds

    if (totalSeconds <= 0) return

    const newTimer: Timer = {
      id: Date.now() + Math.random(),
      name: presetName || newTimerName || `Timer ${timers.length + 1}`,
      minutes,
      seconds,
      totalSeconds,
      remainingSeconds: totalSeconds,
      isRunning: false,
      isComplete: false,
    }
    setTimers((prev) => [...prev, newTimer])
    if (!presetName) {
      setNewTimerName("")
      setNewTimerMinutes("")
      setNewTimerSeconds("")
    }

    // Usage analytics record THAT the tool was used — never what was entered.
    // The privacy policy and FAQ promise exactly this (FRIDGE-293), and
    // tests/tools-capture-no-inputs.test.ts holds every tool to it.
    if (posthog) {
      posthog.capture("tool_calculated", {
        tool_slug: "kitchen-timer",
        tool_name: "Kitchen Timer",
      })
    }
  }

  const startTimer = (id: number) => {
    setTimers((prev) => prev.map((t) => (t.id === id ? { ...t, isRunning: true } : t)))

    intervalRefs.current[id] = setInterval(() => {
      setTimers((prev) =>
        prev.map((timer) => {
          if (timer.id === id && timer.isRunning) {
            const newRemaining = timer.remainingSeconds - 1
            if (newRemaining <= 0) {
              clearInterval(intervalRefs.current[id])
              return { ...timer, remainingSeconds: 0, isRunning: false, isComplete: true }
            }
            return { ...timer, remainingSeconds: newRemaining }
          }
          return timer
        })
      )
    }, 1000)
  }

  const pauseTimer = (id: number) => {
    clearInterval(intervalRefs.current[id])
    setTimers((prev) => prev.map((t) => (t.id === id ? { ...t, isRunning: false } : t)))
  }

  const resetTimer = (id: number) => {
    clearInterval(intervalRefs.current[id])
    setTimers((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, remainingSeconds: t.totalSeconds, isRunning: false, isComplete: false } : t
      )
    )
  }

  const deleteTimer = (id: number) => {
    clearInterval(intervalRefs.current[id])
    setTimers((prev) => prev.filter((t) => t.id !== id))
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow="Kitchen helper"
      heroTitlePre="Multiple timers,"
      heroTitleItalic="one"
      heroTitlePost="kitchen."
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ Real-time", "Multiple concurrent timers"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "165°F", label: "Poultry safe", note: "USDA" },
            { value: "145°F", label: "Whole cuts", note: "+3 min rest, USDA" },
            { value: "40°F", label: "Fridge max", note: "FDA Code" },
            { value: "0°F", label: "Freezer max", note: "USDA" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      // FIX-10 parity: render the FULL metadata FAQ set — the deleted sr-only
      // zone used to carry the items beyond the first six.
      faqs={meta.faqs.map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "kitchen-timer")}
      cta={{
        title: "Shop fridges built for cooks who plan ahead.",
        body: "Browse French door fridges with prep-friendly layouts, full-extension drawers, and dedicated produce zones — the storage that keeps multi-dish meals organized.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ TIMER (single column) ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[840px] mx-auto px-6 md:px-8 space-y-6">
          {/* Add timer card */}
          <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                STEP 01
              </span>
              <div className="flex-1 h-[1px] bg-gray-300" />
            </div>
            <h2
              className="text-gray-900 font-black"
              style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
            >
              Add a timer.
            </h2>
            <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
              Name it, set the time, hit Add. Run as many as you need at once.
            </p>

            <div className="mt-7 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 sm:gap-2">
              <input
                type="text"
                placeholder="Timer name (e.g. Pasta)"
                value={newTimerName}
                onChange={(e) => setNewTimerName(e.target.value)}
                className="tool-input-clean w-full px-4 py-4 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
              />
              <input
                type="number"
                placeholder="Min"
                min={0}
                value={newTimerMinutes}
                onChange={(e) => setNewTimerMinutes(e.target.value)}
                className="tool-input-clean w-full sm:w-24 px-4 py-4 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
              />
              <input
                type="number"
                placeholder="Sec"
                min={0}
                max={59}
                value={newTimerSeconds}
                onChange={(e) => setNewTimerSeconds(e.target.value)}
                className="tool-input-clean w-full sm:w-24 px-4 py-4 text-[16px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
              />
              <button
                onClick={() => addTimer()}
                className="inline-flex items-center justify-center px-6 py-4 rounded-lg bg-[#0469f0] hover:bg-[#0053c4] text-white font-bold text-[15px] transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add
              </button>
            </div>

            {/* Quick presets */}
            <div className="mt-6">
              <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-3">
                Quick presets
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => addTimer(p.label, p.minutes)}
                    className="px-3 py-2 text-[13px] font-semibold border border-gray-300 hover:border-[#ff9951] hover:bg-[#ff9951]/5 rounded-lg transition-colors text-gray-800"
                    title={p.note}
                  >
                    {p.label} <span className="text-gray-500 font-normal">· {p.minutes}m</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Active timers */}
          {timers.length > 0 && (
            <div className="space-y-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-700">
                Active timers ({timers.length})
              </div>
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
                {timers.map((timer) => (
                  <div
                    key={timer.id}
                    className={`rounded-xl p-5 border ${
                      timer.isComplete ? "border-emerald-500 bg-emerald-50" : "border-gray-900 bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <div className="font-bold text-gray-900 text-[16px]">{timer.name}</div>
                        <div className="text-[12px] text-gray-500">
                          {timer.minutes}m {timer.seconds}s set
                        </div>
                      </div>
                      <button
                        onClick={() => deleteTimer(timer.id)}
                        className="text-gray-500 hover:text-rose-600 transition-colors"
                        aria-label="Delete timer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div
                      className={`text-center font-black tabular-nums ${
                        timer.isComplete ? "text-emerald-600" : "text-gray-900"
                      }`}
                      style={{ fontSize: "clamp(36px, 6vw, 48px)", letterSpacing: "-0.04em" }}
                    >
                      {formatTime(timer.remainingSeconds)}
                    </div>
                    {timer.isComplete && (
                      <div className="text-center text-[13px] font-bold text-emerald-700 mt-1">
                        Time&apos;s up
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      {!timer.isRunning && !timer.isComplete && (
                        <button
                          onClick={() => startTimer(timer.id)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-[#0469f0] hover:bg-[#0053c4] text-white font-bold text-[14px] transition-colors"
                        >
                          <Play className="w-3.5 h-3.5 mr-1.5" />
                          Start
                        </button>
                      )}
                      {timer.isRunning && (
                        <button
                          onClick={() => pauseTimer(timer.id)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg bg-[#ff9951] hover:bg-[#ff8533] text-[var(--color-brand-action-text)] font-bold text-[14px] transition-colors"
                        >
                          <Pause className="w-3.5 h-3.5 mr-1.5" />
                          Pause
                        </button>
                      )}
                      <button
                        onClick={() => resetTimer(timer.id)}
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-gray-300 hover:border-gray-900 text-gray-900 font-bold text-[14px] transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Reset
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Built for cooks"
          title="French door fridges with prep-friendly layouts"
          description="Adjustable shelves, full-extension drawers, and bright LED interiors for multi-dish cooking sessions."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Counter-depth fridges that fit kitchens"
          description="Slim profiles for cooks who need every inch of counter space for prep."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
